"use client";
import Cookies from "js-cookie";
import { cookieDomain } from "@/lib/ecommerce/commercetools/client-only/cookies";
import { DefaultCookieSettings } from "@/lib/ecommerce/commercetools/client-only/constants";

export const FX_QUOTE_COOKIE = "edx_fx_quote";

export interface FxQuoteCookiePayload {
  currency: string;
  fxQuoteId: string;
  lockedRate: number;
  expiresAt: string;
}

export function getFxQuoteCookie(): FxQuoteCookiePayload | null {
  if (typeof document === "undefined") return null;
  const raw = Cookies.get(FX_QUOTE_COOKIE);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    if (
      typeof parsed.currency !== "string" ||
      typeof parsed.fxQuoteId !== "string" ||
      typeof parsed.lockedRate !== "number" ||
      typeof parsed.expiresAt !== "string" ||
      !parsed.currency ||
      !parsed.fxQuoteId ||
      parsed.lockedRate <= 0
    ) {
      clearFxQuoteCookie();
      return null;
    }

    const expiresAtMs = new Date(parsed.expiresAt).getTime();
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) {
      clearFxQuoteCookie();
      return null;
    }

    return {
      currency: parsed.currency,
      fxQuoteId: parsed.fxQuoteId,
      lockedRate: parsed.lockedRate,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    clearFxQuoteCookie();
    return null;
  }
}

export function setFxQuoteCookie(payload: FxQuoteCookiePayload): void {
  if (typeof document === "undefined") return;

  const isLocalhost = window.location.hostname === "localhost";
  const domain = cookieDomain();
  const expiresAt = new Date(payload.expiresAt);

  const cookieOptions = isLocalhost
    ? { expires: expiresAt, sameSite: "lax" as const }
    : { expires: expiresAt, domain, ...DefaultCookieSettings };

  Cookies.set(FX_QUOTE_COOKIE, JSON.stringify(payload), cookieOptions);
}

export function clearFxQuoteCookie(): void {
  if (typeof document === "undefined") return;

  const domain = cookieDomain();
  const options = window.location.hostname === "localhost" ? {} : { domain };

  Cookies.remove(FX_QUOTE_COOKIE, options);
}
